import {
  Card,
  CardContent,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
} from "@mui/material";
import InfoPopper from "../popper/InfoPopper";
import { useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import StyledBarChart from "../chart/BarChart";
import ChartCheckbox from "../chart/ChartCheckbox";
import { TFunction } from "i18next";
import Group from "../../models/Group";
import NumberFormatterUtils from "../../utils/numberFormatterUtils";
import { DashboardResults, EmissionTableRow } from "../../models/emission-dashboard.types";
import { useEmissionCardData, getScopeKey } from "./hooks/useEmissionCardData";

export const byChart = 0;
export const byTable = 1;

interface Props {
  t: TFunction<[string, string], undefined>;
  title: string;
  info?: string;
  defaultBy: number;
  showByDropdown?: boolean;
  groups: Group[];
  data: DashboardResults | null;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  preview?: boolean;
  scope?: "Scope-1" | "Scope-2" | "Scope-3" | "Outside-of-Scope";
}

const EmissionCard = (props: Props) => {
  const chartRef = useRef(null);
  const { t, data, startDate, endDate, groups, scope } = props;
  const [by, setBy] = useState(props.defaultBy);

  const [checkRecord, setCheckRecord] = useState<Record<string, boolean>>(
    () => {
      const record: Record<string, boolean> = {};
      groups.forEach((group) => {
        record[group.id] = true;
      });
      return record;
    },
  );

  const onSelectByChange = (e: SelectChangeEvent<number>) => {
    setBy(Number(e.target.value));
  };

  // 🌟 เรียกใช้ Custom Hook ตรงนี้ ทำให้โค้ด UI สะอาดขึ้นมาก!
  const { labels, chartSeries, tableRows, showYoY } = useEmissionCardData({
    data,
    startDate,
    endDate,
    groups,
    scope,
    t,
  });

  useEffect(() => {
    const record: Record<string, boolean> = {};
    groups.forEach((group) => {
      record[group.id] = true;
    });
    setCheckRecord(record);
  }, [groups]);

  // ==========================================
  // RENDER BODY (CHART or TABLE)
  // ==========================================
  let body;

  if (!data) {
    body = (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Typography variant="body2" color="textSecondary">
          {t("No Data Available")}
        </Typography>
      </Box>
    );
  } else if (by === byChart) {
    // --- Render Chart ---
    const filteredSeries = chartSeries.filter((s) => {
      const rootId = (s.id as string).split("_")[0];
      return checkRecord[rootId];
    });

    const checkboxs = groups.map((group) => (
      <ChartCheckbox
        key={group.id}
        text={t(group.title)}
        color={group.colors[1]}
        checked={checkRecord[group.id] ?? false}
        onChange={(_, checked) => {
          setCheckRecord((prev) => ({ ...prev, [group.id]: checked }));
        }}
      />
    ));

    body = (
      <Box>
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          gap={"24px"}
          flexWrap={"wrap"}
          style={{ width: "100%" }}
        >
          {checkboxs}
        </Box>
        <Box flexShrink={0} width={"100%"} sx={{ overflowX: "auto" }}>
          {labels.length > 0 ? (
            <StyledBarChart
              series={filteredSeries}
              seriesLabel="tCO₂e"
              labels={labels}
              preview={props.preview}
            />
          ) : (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                {t("No Data Available")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  } else {
    // --- Render Table ---
    const groupTitleCell = groups.map((item) => {
      if (scope && item.id !== scope) return null;
      return (
        <TableCell key={item.id}>
          <Box display={"flex"} gap={"12px"} alignItems={"center"}>
            <Box width={"24px"} height={"24px"} bgcolor={item.colors[1]} />
            {t(item.title)}
          </Box>
        </TableCell>
      );
    });

    const bodyRow = tableRows.map((row, idx) => {
      const groupCells = groups.map((group) => {
        if (scope && group.id !== scope) return null;
        const scopeKey = getScopeKey(group.id);

        let val = 0;
        let pct = 0;

        if (scope) {
          val = row.total;
          pct = 100;
        } else {
          const scopeData = scopeKey ? (row.scopes[scopeKey] as any) : null;
          val = scopeData?.value ?? 0;
          pct = scopeData?.percent ?? 0;
        }

        if (val === 0)
          return <TableCell key={`${idx}-${group.id}`}>-</TableCell>;

        return (
          <TableCell key={`${idx}-${group.id}`}>
            {NumberFormatterUtils.numberFormat(pct)}%{" "}
            {NumberFormatterUtils.formatDynamic(val)} tCO₂e
          </TableCell>
        );
      });

      let yoyCell;
      if (!showYoY) {
        yoyCell = null;
      } else if (row.yoy === null || row.previousTotal === 0) {
        yoyCell = <TableCell align="right">-</TableCell>;
      } else {
        const trend = row.yoy;
        let trendImg = "/img/trend-down-20240109.svg";
        if (trend >= 0) trendImg = "/img/trend-up-20240109.svg";

        yoyCell = (
          <TableCell>
            <Box display={"flex"} alignItems="center" justifyContent={"end"}>
              <Typography
                variant="body2"
                color={trend >= 0 ? "var(--red)" : "var(--status)"}
                mr={0.5}
              >
                {`${NumberFormatterUtils.numberFormat(trend)}%`}
              </Typography>
              <img
                className={trend >= 0 ? "var(--red)" : "var(--status)"}
                src={trendImg}
                alt="trend"
                width={16}
                height={16}
              />
            </Box>
          </TableCell>
        );
      }

      return (
        <TableRow key={idx}>
          <TableCell>{row.fullLabel}</TableCell>
          {groupCells}
          <TableCell>
            {NumberFormatterUtils.formatDynamic(row.total)} tCO₂e
          </TableCell>
          {yoyCell}
        </TableRow>
      );
    });

    body = (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {groupTitleCell}
              <TableCell>{t("Total")}</TableCell>
              {showYoY && <TableCell>{t("YoY-Change")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>{bodyRow}</TableBody>
        </Table>
      </TableContainer>
    );
  }

  // ==========================================
  // RENDER CARD WRAPPER
  // ==========================================
  return (
    <Card
      ref={chartRef}
      variant="outlined"
      sx={{ borderRadius: "8px", p: "8px" }}
    >
      <CardContent>
        <Box
          display={"flex"}
          flexWrap={"wrap"}
          justifyContent={"end"}
          mb={"30px"}
          gap={"12px"}
          alignItems={"center"}
        >
          <Typography
            variant="body1"
            flexGrow={1}
            sx={{ width: { xs: "100%", sm: "unset" } }}
          >
            {props.title}
            {props.info && <InfoPopper text={props.info} />}
          </Typography>
          {props.showByDropdown && (
            <>
              <img src="/img/setting-5.svg" alt="setting" />
              <FormControl variant="standard">
                <Select value={by} onChange={onSelectByChange}>
                  <MenuItem value={byChart}>{t("By-Chart")}</MenuItem>
                  <MenuItem value={byTable}>{t("By-Table")}</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
        {body}
      </CardContent>
    </Card>
  );
};

export default EmissionCard;
